/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { PagedSortedFilteredRequestParams, SortColumn, TaskDisplay } from '@centecomm/portal-service';
import { useCurrentUser, useUserTasks } from '@centecomm/portal-service-react';
import useTaskMapper from 'Features/Tasks/Hooks/useTaskMapper';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'Shared/Button/Button';
import DynamicContent from 'Shared/PageElements/DynamicContent';
import { PageWrapper } from 'Shared/PageWrapper';
import { Types as TableTypes } from 'Shared/Table';
import useComplianceHubEnabled from 'utils/Hooks/LaunchDarkly/useComplianceHubEnabled';

import { globalMessageActionCreators } from '../../../State';
import styles from './tasks-panel.module.css';

function TasksPage(): React.ReactElement {
    const { t } = useTranslation(['tasks']);
    const history = useHistory();
    const dispatch = useDispatch();
    const { getTasks, dismissTask } = useUserTasks();
    const { isOwnerOperator: currentUserIsOwnerOperator } = useCurrentUser();
    const complianceHubEnabled = useComplianceHubEnabled(true);

    const mapTasks = useTaskMapper(currentUserIsOwnerOperator, complianceHubEnabled);

    const [tasks, setTasks] = useState<TaskDisplay[]>([]);
    const [tasksLoaded, toggleTasksLoaded] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [haveMoreTasks, toggleHaveMoreTasks] = useState<boolean>(false);

    const sortColumn: SortColumn[] = [{ columnName: 'daysOpen', sortDirection: TableTypes.SortDirection.ASC }];

    const loadMoreTasks = () => {
        getTasks(new PagedSortedFilteredRequestParams(sortColumn, [], 10, currentPage)).then(({ results, totalCount }) => {
            toggleHaveMoreTasks(totalCount > results.length);

            const responseTasks: TaskDisplay[] = mapTasks(results);

            setTasks(tasks.concat(responseTasks));
            toggleTasksLoaded(true);
            setCurrentPage(currentPage + 1);
        });
    };

    const markTaskVisitedAndRedirectToLink = async (task: TaskDisplay) => {
        try {
            await dismissTask(`${task.id}`, task.type);
        } catch (err) {
            dispatch(globalMessageActionCreators.addErrorMessage(t('tasks:tasksPage.redirectTaskErrorMessage')));
        } finally {
            history.push(task.link);
        }
    };

    React.useEffect(() => {
        loadMoreTasks();
    }, []);

    return (
        <PageWrapper pageTitle={t('tasks:tasksPage.title')}>
            <h1>{t('tasks:tasksPage.title')}</h1>
            <DynamicContent
                loaded={tasksLoaded}
                noContent={tasks.length === 0}
                noContentMessage={t('tasks:tasksPage.noContentMessage')}
            >
                <>
                    <ul className={styles.TasksList}>
                        {tasks.map((task: TaskDisplay) => (
                            <li
                                key={`task-${task.id}`}
                                className={`${styles.Task} ${task.visited ? styles.Visited : ''}`}
                                onClick={() => markTaskVisitedAndRedirectToLink(task)}
                            >
                                <h2 className={`${styles.Title}`}>{task.title}</h2>
                                <p>{task.subtitle}</p>
                                <p style={{ margin: '0.5rem 0 0', color: 'var(--color-Action)' }}>{task.linkDisplay}</p>
                            </li>
                        ))}
                    </ul>
                    {haveMoreTasks && (
                        <Button callback={loadMoreTasks} text={t('tasks:tasksPage.loadMore')} fullWidth withMarginTop />
                    )}
                </>
            </DynamicContent>
        </PageWrapper>
    );
}

export default TasksPage;
